function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ToggledFeatureKey } from '@domain-group/fe-allhomes-library';
import { AgentRecommendationsGraphQL } from '@domain-group/fe-co-allhomes-agent-recommendations';
import { EventBoundary } from '@domain-group/fe-co-allhomes-analytics';
import { useAllhomesApi } from '@domain-group/fe-co-allhomes-api-client';
import { PureCssStickySidebox, Separator } from '@domain-group/fe-co-allhomes-design-system';
import AllhomesListingCard, { AllhomesListingCardsTabs } from '@domain-group/fe-co-allhomes-listing-card';
import AgentArticles from './components/agent-articles';
import AllhomesAgentBody from './components/agent-body';
import AllhomesAgentModel from './components/agent-model';
import { agentProfileInteractionEvent } from './components/ga-helpers';
import { SideboxCard } from './components/sidebox-card';
import getCardProps from './components/get-card-props';
import { articleProps } from './common-props';
import useSendPageView from './wrappers/use-send-page-view';
import * as styles from './allhomes-agent-profile-page.style';
import { jsx as ___EmotionJSX } from "@emotion/core";
var AllhomesAgentProfilePage = props => {
  var _props$details, _props$details2, _props$details3, _props$details4;
  useSendPageView((_props$details = props.details) === null || _props$details === void 0 ? void 0 : _props$details.id, (_props$details2 = props.details) === null || _props$details2 === void 0 ? void 0 : _props$details2.name);
  var _useAllhomesApi = useAllhomesApi(),
    toggledFeatures = _useAllhomesApi.toggledFeatures;
  var _props$media = props.media,
    smallTablet = _props$media.smallTablet,
    mobile = _props$media.mobile;
  var listingTabNames = {
    sales: 'Sale',
    rentals: 'Rental',
    sold: 'Sold',
    rented: 'Rented'
  };

  // GA event handlers
  var listingTabClickHandler = tab => {
    agentProfileInteractionEvent("Listings Tab - ".concat(listingTabNames[tab.id]));
  };
  var listingCardClickHandler = (listingId, tab) => {
    agentProfileInteractionEvent("Property Click - ".concat(listingTabNames[tab.id]));
  };
  var articlesLayout = () => {
    if (mobile) {
      return 'column';
    }
    if (smallTablet) {
      return 'two-row';
    }
    return 'row';
  };
  return ___EmotionJSX(Fragment, null, ___EmotionJSX(EventBoundary, {
    name: "agent"
  }, ___EmotionJSX(AllhomesAgentBody, {
    profile: props.details,
    maxPreviewHeight: props.maxPreviewHeight,
    onEmailClick: props.onEmailClick
  }), props.articles.length > 0 && ___EmotionJSX(AgentArticles, {
    layout: articlesLayout(),
    articles: props.articles
  })), props.initialListings && ___EmotionJSX(AllhomesListingCardsTabs, {
    css: styles.listingCards,
    title: "Listings by ".concat(props.details.name),
    titleTagType: "h2",
    sourceId: props.details.id,
    sourceType: "agent",
    className: "allhomes-agent-profile__allhomes-listing-card-list",
    eventHandlers: {
      tabClicked: listingTabClickHandler,
      listingCardClicked: listingCardClickHandler,
      showAllClicked: props.onShowAll,
      showLessClicked: props.onShowLess
    },
    tabs: [{
      id: 'sales',
      label: 'For Sale',
      labelMobile: 'Sale',
      totalCount: props.initialListings.sales.total,
      initialSearchResult: props.initialListings.sales.listings,
      subTitle: "".concat(props.initialListings.sales.total, " ").concat(props.initialListings.sales.total > 1 ? 'properties' : 'property', " for sale"),
      duration: ''
    }, {
      id: 'rentals',
      label: 'For Rent',
      labelMobile: 'Rent',
      totalCount: props.initialListings.rentals.total,
      initialSearchResult: props.initialListings.rentals.listings,
      subTitle: "".concat(props.initialListings.rentals.total, " ").concat(props.initialListings.rentals.total > 1 ? 'properties' : 'property', " for rent"),
      duration: ''
    }, {
      id: 'sold',
      label: 'Sold',
      labelMobile: 'Sold',
      totalCount: props.initialListings.recentlySold.total,
      initialSearchResult: props.initialListings.recentlySold.listings,
      subTitle: "".concat(props.initialListings.recentlySold.total, " ").concat(props.initialListings.recentlySold.total > 1 ? 'properties' : 'property', " sold"),
      duration: "Last ".concat(props.listingVisibility.sale.duration, " ").concat(props.listingVisibility.sale.unit, "s")
    }, {
      id: 'rented',
      label: 'Rented',
      labelMobile: 'Rented',
      totalCount: props.initialListings.recentlyRented.total,
      initialSearchResult: props.initialListings.recentlyRented.listings,
      subTitle: "".concat(props.initialListings.recentlyRented.total, " ").concat(props.initialListings.recentlyRented.total > 1 ? 'properties' : 'property', " rented"),
      duration: "Last ".concat(props.listingVisibility.rent.duration, " ").concat(props.listingVisibility.rent.unit, "s")
    }]
  }), props.details.reviewProfile && ___EmotionJSX(Fragment, null, ___EmotionJSX(Separator, {
    css: styles.separator
  }), ___EmotionJSX("div", {
    css: styles.recommendations
  }, ___EmotionJSX(AgentRecommendationsGraphQL, {
    version: toggledFeatures[ToggledFeatureKey.FIND_AN_AGENT_PACKAGE_BRANDING] ? 'new' : 'old',
    reviewProfile: props.details.reviewProfile,
    heading: (_props$details3 = props.details) !== null && _props$details3 !== void 0 && _props$details3.name ? "".concat(props.details.name.split(' ')[0], "'s reviews") : undefined,
    brandingLevel: (_props$details4 = props.details) === null || _props$details4 === void 0 ? void 0 : _props$details4.brandingLevel
  }), toggledFeatures[ToggledFeatureKey.FIND_AN_AGENT_PACKAGE_BRANDING] && ___EmotionJSX("div", {
    css: styles.sticky
  }, ___EmotionJSX(PureCssStickySidebox, {
    topEdgeOffset: 81
  }, ___EmotionJSX(SideboxCard, _extends({}, getCardProps(props.details), {
    onEmailClick: props.onEmailClick
  })))))));
};
AllhomesAgentProfilePage.propTypes = {
  details: PropTypes.shape(AllhomesAgentModel).isRequired,
  onShowAll: PropTypes.func.isRequired,
  onShowLess: PropTypes.func.isRequired,
  maxPreviewHeight: PropTypes.number,
  initialListings: PropTypes.shape({
    sales: PropTypes.shape({
      listings: PropTypes.arrayOf(PropTypes.shape(AllhomesListingCard.propTypes)),
      total: PropTypes.number
    }),
    rentals: PropTypes.shape({
      listings: PropTypes.arrayOf(PropTypes.shape(AllhomesListingCard.propTypes)),
      total: PropTypes.number
    }),
    recentlySold: PropTypes.shape({
      listings: PropTypes.arrayOf(PropTypes.shape(AllhomesListingCard.propTypes)),
      total: PropTypes.number
    }),
    recentlyRented: PropTypes.shape({
      listings: PropTypes.arrayOf(PropTypes.shape(AllhomesListingCard.propTypes)),
      total: PropTypes.number
    })
  }),
  listingVisibility: PropTypes.shape({
    sale: PropTypes.shape({
      duration: PropTypes.string,
      unit: PropTypes.string
    }),
    rent: PropTypes.shape({
      duration: PropTypes.string,
      unit: PropTypes.string
    })
  }),
  media: PropTypes.shape({
    smallTablet: PropTypes.bool,
    mobile: PropTypes.bool
  }),
  articles: PropTypes.arrayOf(PropTypes.shape(articleProps)),
  onEmailClick: PropTypes.func
};
AllhomesAgentProfilePage.defaultProps = {
  maxPreviewHeight: undefined,
  initialListings: undefined,
  listingVisibility: {
    sale: {
      duration: '',
      unit: ''
    },
    rent: {
      duration: '',
      unit: ''
    }
  },
  media: {},
  articles: [],
  onEmailClick: () => {}
};
export default AllhomesAgentProfilePage;